import axios from "axios";
import { properties } from "../config/ClientConfigurations.js";
import { jwtDecode } from "jwt-decode";

class ApiService {
  isTokenOk() {
    const token = localStorage.getItem("token");
    return token && !this.isTokenExpired(token);
  }

  getAccessToken() {
    const token = localStorage.getItem("token");
    if (token && !this.isTokenExpired(token)) {
      return token;
    }

    console.error("Token does not exist or is expired");
    this.logout();
  }

  getId() {
    const id = localStorage.getItem("id");
    if (id) return id;
    throw new Error("Id does not exist in local storage");
  }

  getEmail() {
    const email = localStorage.getItem("email");
    if (email) {
      return email;
    }

    throw new Error("Email does not exist in local storage");
  }

  async getMe() {
    return this.get("api/v1/users/me");
  }

  async login(email, password) {
    try {
      const tokenResponse = await axios.post(`${properties.apiUrl}/api/auth`, { email: email, password: password });

      if (tokenResponse && tokenResponse.data) {
        const accessToken = tokenResponse.data.token;
        // console.log(accessToken);
        const claims = this.getClaims(accessToken);
        // console.log(claims);
        if (claims) {
          const id = claims["_id"];
          const email = claims["email"];
          if (id && email && password) {
            localStorage.setItem("token", accessToken);
            localStorage.setItem("id", id);
            localStorage.setItem("email", email);
          } else {
            throw new Error("Either id, email or access token claims are not correct.");
          }
        } else {
          throw new Error("There is an error with the access token.");
        }
      }
    } catch (error) {
      throw error;
    }
  }

  logout() {
    localStorage.removeItem("token");
    localStorage.removeItem("id");
    localStorage.removeItem("email");
    window.location.href = "/auth/login";
  }

  getClaims(token) {
    if (token) {
      try {
        return jwtDecode(token);
      } catch (error) {
        console.error("Error fetching token:", error);
        return null;
      }
    } else {
      return null;
    }
  }

  isTokenExpired(token) {
    try {
      const decodedToken = jwtDecode(token);
      const currentTime = Math.floor(Date.now() / 1000);
      return decodedToken.exp < currentTime;
    } catch (error) {
      console.error("Error decoding token:", error);
      return true;
    }
  }

  async get(path, queryParams) {
    return this.makeRequest(path, "GET", null, undefined, queryParams);
  }

  async post(path, requestBody) {
    return this.makeRequest(path, "POST", null, requestBody);
  }

  async put(path, requestBody) {
    return this.makeRequest(path, "PUT", null, requestBody);
  }

  async postJson(path, requestBody) {
    return this.makeRequest(path, "POST", { "Content-Type": "application/json" }, requestBody);
  }

  async putJson(path, requestBody) {
    return this.makeRequest(path, "PUT", { "Content-Type": "application/json" }, requestBody);
  }

  delete(path, queryParams) {
    return this.makeRequest(path, "DELETE", null, undefined, queryParams);
  }

  async makeRequest(url, method, headers, requestBody, queryParams) {
    const token = this.getAccessToken();
    if (headers) {
      headers["Authorization"] = `Bearer ${token}`;
    } else {
      headers = { Authorization: `Bearer ${token}` };
    }
    const config = {
      method: method,
      url: `${properties.apiUrl}/${url}`,
      params: queryParams,
      data: requestBody,
      headers: headers,
    };

    try {
      const response = await axios(config);
      return response.data;
    } catch (error) {
      if (error.response.status === 401 || error.response.status === 403) this.logout();
      console.log(error);
      throw new Error(error);
    }
  }
}

export const apiService = new ApiService();
